import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Award, Loader2, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function DiplomaTemplateUploader({ eventId, template, templateType, textColor, onChange }) {
  const [busy, setBusy] = useState(false);
  const [color, setColor] = useState(textColor || '#1f2937');

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('text_color', color);
      const r = await axios.post(`${API}/admin/events/${eventId}/diploma-template`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(r.data);
      toast.success('Plantilla de diploma guardada');
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo subir la plantilla'); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('¿Quitar la plantilla de diploma? Los pilotos no podrán descargarlo.')) return;
    setBusy(true);
    try { await axios.delete(`${API}/admin/events/${eventId}/diploma-template`); onChange({ diploma_template: null, diploma_template_type: null }); toast.success('Plantilla eliminada'); }
    catch { toast.error('No se pudo eliminar'); } finally { setBusy(false); }
  };

  return (
    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 space-y-2" data-testid="diploma-template-uploader">
      <p className="text-sm font-semibold text-gray-900 flex items-center gap-1"><Award className="w-4 h-4 text-amber-600" /> Diploma / sello digital</p>
      <p className="text-xs text-gray-600">Sube el diseño del diploma (imagen PNG/JPG o PDF, formato A4 horizontal). Al completar un reto, el piloto descargará el PDF con su nombre, el reto, el evento, la fecha, los waypoints y los puntos superpuestos en el centro. Deja libre la mitad inferior-central del diseño.</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="diploma_file" className="text-xs">Diseño</Label>
          <input id="diploma_file" type="file" accept="image/png,image/jpeg,application/pdf" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} className="block text-sm" data-testid="diploma-template-input" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="diploma_color" className="text-xs">Color del texto</Label>
          <input id="diploma_color" type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-9 w-14 rounded border border-gray-300 bg-white" data-testid="diploma-text-color-input" />
        </div>
        {busy && <Loader2 className="w-5 h-5 animate-spin text-amber-600" />}
      </div>
      {template ? (
        <div className="flex items-center gap-2 text-xs" data-testid="diploma-template-current">
          <span className="text-emerald-700 font-medium">✓ Plantilla cargada ({templateType === 'pdf' ? 'PDF' : 'imagen'})</span>
          <a href={template} target="_blank" rel="noreferrer" className="text-blue-700 underline inline-flex items-center gap-1">Ver <ExternalLink className="w-3 h-3" /></a>
          <Button type="button" size="sm" variant="ghost" onClick={remove} disabled={busy} className="h-7 text-red-600 px-2" data-testid="diploma-template-remove-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Quitar</Button>
        </div>
      ) : <p className="text-xs text-amber-700">Sin plantilla: los pilotos no verán el botón de diploma.</p>}
    </div>
  );
}
